/**
 * Sanitized generic-error instrumentation (Sentry RCA #3, JAVASCRIPT-REACT MP/QZ/WC).
 *
 * Network-level failures (10s callApi timeout / offline) and thunk-internal JS
 * exceptions used to be funnelled into the generic "Something went wrong"
 * snackbar with `debugInfo:{status:null,code:null,name:null}` and NO endpoint —
 * untriageable in Sentry. These pure helpers classify the failure and attach a
 * sanitized failing endpoint + a stable `name`, so callApi callers can dispatch
 * an enriched `updateError` payload that errorHandler forwards to Sentry.
 *
 * Safety: NEVER read request bodies, headers, or the raw Axios error object —
 * only status/code/name, a caller-supplied endpoint template and HTTP method.
 */

// Intentional cancellations (AbortController / axios cancel) must be silent:
// no snackbar and no Sentry event.
const CANCELLATION_NAMES = new Set(['CanceledError', 'AbortError']);
const CANCELLATION_CODES = new Set(['ERR_CANCELED']);
const HTTP_METHODS = new Set(['DELETE', 'GET', 'HEAD', 'OPTIONS', 'PATCH', 'POST', 'PUT']);
const SAFE_ENDPOINT_SEGMENTS = new Set([
    'api',
    'v1',
    'forms',
    'lead-treatments',
    'lead-treatments-flat',
    'leads',
    'leads-flat',
    'practices',
    'reports',
    'share',
]);
const SAFE_ENDPOINT_PLACEHOLDERS = new Set([':id', ':redacted', ':token', ':uuid']);
const MAX_DIAGNOSTIC_MESSAGE_LENGTH = 500;
const MAX_STACK_LENGTH = 4000;

/**
 * Strip a URL down to a low-cardinality, PII-safe path:
 *  - drop query string and fragment, and any protocol/host
 *  - template numeric IDs (`123` → `:id`), UUIDs (`:uuid`) and long opaque
 *    tokens containing a digit (`:token`)
 *  - fail closed for every unknown or encoded segment (`:redacted`)
 * so Sentry groups by real endpoint without retaining patient/name-like values.
 * Callers should still supply a static endpoint template rather than a live URL.
 */
export function sanitizeEndpoint(uri) {
    if (!uri || typeof uri !== 'string') return null;
    let path = uri.split('?')[0].split('#')[0];
    path = path.replace(/^https?:\/\/[^/]+/i, '');
    if (!path.startsWith('/')) return null;
    const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return path
        .split('/')
        .map((seg) => {
            if (!seg) return seg;
            if (SAFE_ENDPOINT_PLACEHOLDERS.has(seg)) return seg;
            if (/^\d+$/.test(seg)) return ':id';
            if (uuid.test(seg)) return ':uuid';
            // Long opaque token that carries at least one digit (avoids
            // templating ordinary path words like "lead-treatments-flat").
            if (seg.length >= 16 && /\d/.test(seg) && /^[A-Za-z0-9_-]+$/.test(seg)) return ':token';
            if (SAFE_ENDPOINT_SEGMENTS.has(seg)) return seg;
            return ':redacted';
        })
        .join('/');
}

const sanitizeMethod = (method) => {
    const normalized = typeof method === 'string' ? method.toUpperCase() : null;
    return HTTP_METHODS.has(normalized) ? normalized : null;
};

const sanitizeDiagnosticText = (value, maxLength) => {
    if (typeof value !== 'string' || !value) return null;
    return value
        .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[redacted-email]')
        .replace(/https?:\/\/[^\s)]+/gi, (url) => sanitizeEndpoint(url) || '[redacted-url]')
        .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '[redacted-uuid]')
        .replace(/\b[A-Za-z0-9_-]{24,}\b/g, '[redacted-token]')
        .replace(/\b(?:\+?\d[\d\s().-]{7,}\d)\b/g, '[redacted-number]')
        .slice(0, maxLength);
};

const sanitizeErrorName = (name) => (
    typeof name === 'string' && /^[A-Za-z][A-Za-z0-9]{0,63}$/.test(name)
        ? name
        : 'Error'
);

const sanitizeErrorCode = (code) => (
    typeof code === 'string' && /^[A-Z0-9_:-]{1,64}$/.test(code)
        ? code
        : null
);

const buildSanitizedCause = (error, name, diagnosticMessage) => {
    const cause = new Error(diagnosticMessage);
    cause.name = name;
    const rawFrames = typeof error.stack === 'string'
        ? error.stack.split('\n').slice(1).join('\n')
        : '';
    const safeFrames = sanitizeDiagnosticText(rawFrames, MAX_STACK_LENGTH);
    cause.stack = safeFrames
        ? `${name}: ${diagnosticMessage}\n${safeFrames}`
        : `${name}: ${diagnosticMessage}`;
    return cause;
};

/**
 * Build a sanitized updateError payload from a failed callApi result or a
 * thrown error. Returns `{ silent: true }` for intentional cancellations so the
 * caller skips dispatching entirely.
 *
 * @param {object|Error} source a callApi response ({ status, error }) or a raw Error
 * @param {object} context caller-supplied static endpoint template and HTTP method
 */
export function buildApiErrorPayload(source, { endpointTemplate, method } = {}) {
    const isResponse = source && typeof source === 'object' && !(source instanceof Error) && 'status' in source;
    const err = isResponse ? source.error : source;
    const isErrorObj = err instanceof Error;
    const httpStatus = isResponse ? source.status : null;
    const code = isErrorObj ? sanitizeErrorCode(err.code) : null;
    const name = isErrorObj ? sanitizeErrorName(err.name) : null;
    const endpoint = sanitizeEndpoint(endpointTemplate);
    const sanitizedMethod = sanitizeMethod(method);
    const isAxiosError = isErrorObj && (err.isAxiosError === true || name === 'AxiosError');

    if (CANCELLATION_CODES.has(code) || CANCELLATION_NAMES.has(name)) {
        return { silent: true };
    }

    if (isAxiosError && code === 'ECONNABORTED') {
        return {
            success: false,
            message: 'The request timed out. Please try again.',
            status: 0,
            code: 'ECONNABORTED',
            name: 'TimeoutError',
            endpoint,
            method: sanitizedMethod,
        };
    }

    const noHttp = httpStatus === 0 || httpStatus == null;
    if (isErrorObj && noHttp) {
        // Axios owns several ERR_* codes, but that namespace is not unique to
        // Axios. Require Axios identity so a library/internal exception such as
        // TypeError + ERR_INVALID_STATE keeps its diagnostic message and stack.
        const normalizedName = isAxiosError ? 'NetworkError' : (name || 'Error');
        if (!isAxiosError) {
            const diagnosticMessage = sanitizeDiagnosticText(err.message, MAX_DIAGNOSTIC_MESSAGE_LENGTH)
                || 'Unexpected client exception';
            return {
                success: false,
                message: 'Something went wrong',
                status: 0,
                code,
                name: normalizedName,
                causeClass: normalizedName,
                diagnosticMessage,
                cause: buildSanitizedCause(err, normalizedName, diagnosticMessage),
                endpoint,
                method: sanitizedMethod,
            };
        }
        return {
            success: false,
            message: 'A network error occurred. Please try again.',
            status: 0,
            code,
            name: normalizedName,
            endpoint,
            method: sanitizedMethod,
        };
    }

    // Has an HTTP status (server/other), or a non-Error local block (e.g. the
    // callApi "Invalid path parameter" sentinel): pass through with a generic
    // user message but still carry the sanitized endpoint for triage.
    return {
        success: false,
        message: 'Something went wrong',
        status: httpStatus ?? 0,
        code,
        name,
        endpoint,
        method: sanitizedMethod,
    };
}
